import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { DocumentAttributeFieldOption } from '../../../shared/lib/compliance-shared/document-attributes-form/document-attributes-form.store';
import { PagingToken } from '../../../shared/models/core';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { Requirement } from '../../models/requirement';
import { DocumentTypeConfiguration } from '../../models/requirement-descriptions.model';
import { RequirementService } from '../../requirement.service';
import { DocumentTypeConfigurationRequirementDto } from '../models/document-type-configuration-requirement-dto.model';
import { MetadataFieldOptionDto } from '../models/metadata-field-option-dto.model';
import { PagingTokenOfRequirementDto } from '../models/paging-token-of-requirement-dto.model';
import { RequirementDescriptionClient } from '../requirement-description-client.service';
import { RequirementsClient } from '../requirements-client.service';
import { NSwagMigrationService } from './nswag-migration.service';

// 5 endpoints
@Injectable({ providedIn: 'root' })
export class RequirementNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'RequirementNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationRequirementService;
    private readonly legacy = inject(RequirementService);
    private readonly requirementsClient = inject(RequirementsClient);
    private readonly requirementDescriptionClient = inject(RequirementDescriptionClient);


    // ** RequirementsController.GetRequirements
    getRequirements(idList: number[]): Observable<PagingToken<Requirement[]> | PagingTokenOfRequirementDto> {
        return this.invoke(x => x
            ? this.legacy.getRequirements(idList)
            : this.requirementsClient.getRequirements(idList, undefined, undefined));
    }

    // ** RequirementsController.UpdateRequirementExpirationDate
    updateRequirementExpirationDate(requirementId: number, newDate: Date): Observable<Object | void> {
        return this.invoke(x => x
            ? this.legacy.updateRequirementExpirationDate(requirementId, newDate)
            : this.requirementsClient.updateRequirementExpirationDate(requirementId, { expirationDate: newDate }));
    }

    // ** RequirementsController.UpdateRequirementLicensureState
    updateRequirementLicensureState(requirementId: number, selectedOption: DocumentAttributeFieldOption | MetadataFieldOptionDto) {
        return this.invoke(x => x
            ? this.legacy.updateRequirementLicensureState(requirementId, selectedOption as DocumentAttributeFieldOption)
            : this.requirementsClient.updateRequirementLicensureState(requirementId, selectedOption));
    }

    // ** RequirementsController.GetLicensureStates
    getLicensureStates(): Observable<DocumentAttributeFieldOption[] | MetadataFieldOptionDto[]> {
        return this.invoke(x => x
            ? this.legacy.getLicensureStates()
            : this.requirementsClient.getLicensureStates());
    }

    // RequirementDescriptionController.GetWinningDescriptionsByContractId
    getDocumentTypeRequirementDescriptionByContractId(
        requirementId: number,
        contractId: number,
        includeInternal: boolean,
        includeExternal: boolean): Observable<DocumentTypeConfiguration[] | DocumentTypeConfigurationRequirementDto[]> {
        return this.invoke(x => x
            ? this.legacy.getDocumentTypeRequirementDescriptionByContractId(requirementId, contractId, includeInternal, includeExternal)
            : this.requirementDescriptionClient.getWinningDescriptionsByContractId(requirementId, contractId, includeInternal, includeExternal))
            .pipe(tap((requirements) => {
                requirements.sort((a, b) => {
                    return a.documentTypeDescriptionTypeDisplayOrder - b.documentTypeDescriptionTypeDisplayOrder;
                });
            }));
    }
}
